import {
  CreateQuoteDocument,
  ImportDocumentRequest,
  QuoteDocumentConfig,
  QuoteDocumentConfigPositionEnum,
} from '@monetize/types/app';
import { FC, useEffect, useState } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doCreateQuoteDocument,
  doGetQuoteDocumentConfigDetails,
  doImportQuoteDocument,
  doPrintQuoteToPdf,
  useDeleteQuoteDocumentConfig,
  useGetAttachedQuoteDocuments,
  useGetAvailableQuoteDocuments,
  useUpdateDocumentConfiguration,
  useUpdateDocumentConfigurations,
} from '../../../../api/cpqService';
import { enqueueForQuote } from '../../../../api/quoteWriteChain';
import { useGetQuoteDocumentTypes } from '../../../../api/settingsService';
import { MAlert, MBox } from '../../../../components/Monetize';
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  getSafeFilename,
} from '../../../../utils';
import {
  downloadBlobAsFile,
  showPdfInNewTab,
} from '../../../../utils/download';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteDocumentsMode } from './QuoteDocumentsList/constants';
import { QuoteDocumentsList } from './QuoteDocumentsList/QuoteDocumentsList';
import {
  buildDocumentUploadFormData,
  orchestrateDocumentReplace,
} from './quoteDocumentsListUtils';

interface AttachedQuoteDocumentsProps {
  quoteId: string;
  isReadOnly: boolean;
}

export const AttachedQuoteDocuments: FC<AttachedQuoteDocumentsProps> = ({
  quoteId,
  isReadOnly,
}) => {
  const [beforeDocuments, setBeforeDocuments] = useState<QuoteDocumentConfig[]>(
    [],
  );
  const [afterDocuments, setAfterDocuments] = useState<QuoteDocumentConfig[]>(
    [],
  );

  const [isImportingDocuments, setIsImportingDocuments] = useState(false);

  const { data: quoteDocumentTypes } = useGetQuoteDocumentTypes();

  const { data: quoteDocumentConfigs, refetch: refetchQuoteDocumentConfigs } =
    useGetAttachedQuoteDocuments(quoteId);

  const {
    data: availableDocuments,
    isLoading: isAvailableDocumentsLoading,
    refetch: refetchAvailableDocuments,
  } = useGetAvailableQuoteDocuments(quoteId);

  const { mutateAsync: deleteDocumentConfig } = useDeleteQuoteDocumentConfig();

  const {
    mutate: updateDocumentConfigurations,
    mutateAsync: updateDocumentConfigurationsAsync,
  } = useUpdateDocumentConfigurations(quoteId);

  const { mutateAsync: updateDocumentConfiguration } =
    useUpdateDocumentConfiguration(quoteId);

  const {
    quoteData: { quote, evaluateQuoteRules },
  } = useQuoteContext();

  // Update local documents state when data changes
  useEffect(() => {
    if (quoteDocumentConfigs) {
      const before = quoteDocumentConfigs
        .filter(
          (doc) => doc.position === QuoteDocumentConfigPositionEnum.BEFORE,
        )
        .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
      const after = quoteDocumentConfigs
        .filter((doc) => doc.position === QuoteDocumentConfigPositionEnum.AFTER)
        .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
      setBeforeDocuments(before);
      setAfterDocuments(after);
    }
  }, [quoteDocumentConfigs]);

  const handleOpenDocument = async (
    documentConfigId: string,
    filename?: string,
  ) => {
    try {
      const documentConfig = await doGetQuoteDocumentConfigDetails(
        quoteId,
        documentConfigId,
        true,
      );

      if (documentConfig.storageObject.contentBase64Encoded && filename) {
        showPdfInNewTab(
          documentConfig.storageObject.contentBase64Encoded,
          filename,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleOpenQuote = async () => {
    try {
      const content = await doPrintQuoteToPdf(quoteId, true, true);
      const base64Data = await arrayBufferToBase64(content);
      const filename = getSafeFilename(quote?.description || 'Quote');
      showPdfInNewTab(base64Data, `${filename}.pdf`);
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleDownloadDocument = async (doc: QuoteDocumentConfig) => {
    try {
      const documentConfig = await doGetQuoteDocumentConfigDetails(
        quoteId,
        doc.id,
        true,
        doc.storageObject.mediaType,
      );
      if (documentConfig.storageObject.contentBase64Encoded) {
        downloadBlobAsFile(
          base64ToArrayBuffer(
            documentConfig.storageObject.contentBase64Encoded,
          ),
          documentConfig.storageObject.filename,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleDownloadQuote = async () => {
    try {
      const content = await doPrintQuoteToPdf(quoteId, true, true);
      const filename = getSafeFilename(quote?.description || 'Quote');
      downloadBlobAsFile(content, `${filename}.pdf`);
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleDeleteDocument = async (documentConfigId: string) => {
    try {
      // The delete mutation runs on the quote chain; the evaluation queues
      // behind it.
      await deleteDocumentConfig({ quoteId, documentConfigId });
      await evaluateQuoteRules(quoteId);
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  // Calls the plain API function directly (not a mutation hook) so this
  // doesn't also trigger an automatic invalidateQueries-driven refetch that
  // would race with, and duplicate, the explicit refetch below — which is why
  // it is enqueued on the quote chain here rather than inside a hook.
  const submitDocumentFile = (data: CreateQuoteDocument, file: File) =>
    enqueueForQuote(quoteId, async () => {
      await doCreateQuoteDocument(
        quoteId,
        buildDocumentUploadFormData(data, file),
      );
      // Await only the document list so the new row is in before/afterDocuments
      // before the caller clears its pending upload placeholder. Rule
      // evaluation is slower and unrelated to the document list rendering, so
      // it runs in the background instead of delaying that placeholder swap.
      // It must not be awaited: it queues behind this upload on the same chain.
      const { data: refreshedConfigs } = await refetchQuoteDocumentConfigs();
      evaluateQuoteRules(quoteId);
      return refreshedConfigs;
    });

  const handleCreateDocument = async (
    data: CreateQuoteDocument,
    file: File,
  ) => {
    await submitDocumentFile(data, file);
  };

  const handleReplaceDocument = async (
    doc: QuoteDocumentConfig,
    file: File,
  ) => {
    try {
      // deleteDocument and persistReorder are mutation hooks, which run on the
      // quote chain themselves; createDocument enqueues itself. Each step is
      // awaited here, outside the chain, so they never nest.
      await orchestrateDocumentReplace(doc, file, {
        beforeDocuments,
        afterDocuments,
        deleteDocument: (documentConfigId) =>
          deleteDocumentConfig({ quoteId, documentConfigId }),
        createDocument: submitDocumentFile,
        persistReorder: updateDocumentConfigurationsAsync,
      });
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleChangeDocumentType = async (
    doc: QuoteDocumentConfig,
    documentTypeId: string,
  ) => {
    try {
      await updateDocumentConfiguration({
        id: doc.id,
        documentTypeId,
        position: doc.position,
        sortWithinPosition: doc.sortWithinPosition,
        autoAddToQuotes: doc.autoAddToQuotes,
      });
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleImportDocuments = async (items: ImportDocumentRequest[]) => {
    setIsImportingDocuments(true);
    try {
      // Calls the plain API function directly (not the useImportDocument
      // mutation) so a batch of N imports doesn't invalidate — and
      // refetch — the document list and available-documents list N times.
      // Imports are still serialized per quoteId, then both lists are
      // refetched exactly once at the end.
      await Promise.all(
        items.map((item) =>
          enqueueForQuote(quoteId, () => doImportQuoteDocument(quoteId, item)),
        ),
      );
      await refetchQuoteDocumentConfigs();
      await refetchAvailableDocuments();
      await evaluateQuoteRules(quoteId);
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setIsImportingDocuments(false);
    }
  };

  if (
    beforeDocuments.length === 0 &&
    afterDocuments.length === 0 &&
    isReadOnly
  ) {
    return (
      <MAlert type="info" pl={4}>
        No documents added
      </MAlert>
    );
  }

  return (
    <MBox p={4}>
      <QuoteDocumentsList
        mode={QuoteDocumentsMode.QUOTE}
        quoteId={quoteId}
        ownerId={quoteId}
        isReadOnly={isReadOnly}
        beforeDocuments={beforeDocuments}
        afterDocuments={afterDocuments}
        quoteDocumentTypes={quoteDocumentTypes || []}
        availableDocuments={availableDocuments || []}
        isAvailableDocumentsLoading={isAvailableDocumentsLoading}
        isImportingDocuments={isImportingDocuments}
        onOpenDocument={handleOpenDocument}
        onDeleteDocument={handleDeleteDocument}
        onDownloadDocument={handleDownloadDocument}
        onOpenQuote={handleOpenQuote}
        onDownloadQuote={handleDownloadQuote}
        onChangeDocumentType={handleChangeDocumentType}
        onReplaceDocument={handleReplaceDocument}
        onCreateDocument={handleCreateDocument}
        onUpdateDocumentConfigurations={updateDocumentConfigurations}
        onImportDocuments={handleImportDocuments}
      />
    </MBox>
  );
};
